import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LOGOUT_FAILED, useAuth } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';
import {
  LayoutDashboard, Users, AlertTriangle, CreditCard, FileText, Settings, LogOut, Menu, X, Inbox, UserCheck, BookOpen, ShieldCheck, Palette
} from 'lucide-react';

export default function AdminShell({ children }) {
  const { user, logout } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [sideOpen, setSideOpen] = useState(false);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      toast.error(LOGOUT_FAILED);
      return;
    }
    navigate('/');
  };

  const links = [
    { path: '/admin/dashboard', icon: <LayoutDashboard size={16} />, label: 'Tableau de bord', testid: 'admin-nav-dashboard' },
    { path: '/admin/users', icon: <Users size={16} />, label: 'Utilisateurs', testid: 'admin-nav-users' },
    { path: '/admin/moderation', icon: <AlertTriangle size={16} />, label: 'Modération', testid: 'admin-nav-moderation' },
    { path: '/admin/transactions', icon: <CreditCard size={16} />, label: 'Transactions', testid: 'admin-nav-transactions' },
    { path: '/admin/disputes', icon: <AlertTriangle size={16} />, label: 'Litiges', testid: 'admin-nav-disputes' },
    { path: '/admin/audit', icon: <FileText size={16} />, label: 'Audit', testid: 'admin-nav-audit' },
    { path: '/admin/emails', icon: <Inbox size={16} />, label: 'Emails (dev)', testid: 'admin-nav-emails' },
    { path: '/admin/role-requests', icon: <UserCheck size={16} />, label: 'Demandes de rôles', testid: 'admin-nav-role-requests' },
    { path: '/admin/docs', icon: <BookOpen size={16} />, label: 'Documentation', testid: 'admin-nav-docs' },
    { path: '/admin/design', icon: <Palette size={16} />, label: 'Système de design', testid: 'admin-nav-design' },
    { path: '/admin/securite', icon: <ShieldCheck size={16} />, label: 'Sécurité du compte', testid: 'admin-nav-security' },
  ];

  const Sidebar = () => (
    <aside className={`c2s-side${sideOpen ? ' open' : ''}`} style={{ width: 220 }}>
      <div className="side-logo flex gap-8 items-center">
        <div className="side-logo-mark" style={{ background: 'var(--red)', color: '#fff' }}>A</div>
        <div>
          <div className="side-logo-name">Colis<em>2</em>Ship</div>
          <div style={{ fontSize: '10px', color: 'var(--red)', fontWeight: 600 }}>ADMIN</div>
        </div>
      </div>
      <nav className="side-nav">
        {links.map(l => (
          <button key={l.path} data-testid={l.testid}
            className={`side-link${isActive(l.path) ? ' active' : ''}`}
            onClick={() => { navigate(l.path); setSideOpen(false); }}
          >
            {l.icon} {l.label}
          </button>
        ))}
      </nav>
      <div className="side-user">
        <div className="av av-red" style={{ width: 32, height: 32, fontSize: 12 }}>
          {((user?.first_name || '')[0] || '') + ((user?.last_name || '')[0] || '')}
        </div>
        <div className="side-user-info">
          <div className="side-user-name">{user?.first_name} {user?.last_name}</div>
          <div className="side-user-role" style={{ color: 'var(--red)' }}>Administrateur</div>
        </div>
        <button data-testid="admin-btn-logout" style={{ background: 'none', border: 'none', color: 'var(--ink3)', cursor: 'pointer' }} onClick={handleLogout}>
          <LogOut size={16} />
        </button>
      </div>
    </aside>
  );

  return (
    <div className="admin-layout">
      <Sidebar />
      {sideOpen && <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 199 }} onClick={() => setSideOpen(false)} />}
      <main className="admin-main">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }} className="md:hidden">
          <button style={{ background: 'none', border: 'none', cursor: 'pointer', marginRight: 12 }}
            onClick={() => setSideOpen(!sideOpen)}>
            {sideOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
          <span style={{ fontWeight: 700 }}>Admin Panel</span>
        </div>
        {children}
      </main>
    </div>
  );
}
